import React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import ViewCopyAccordion from "./ViewCopyAccordion";
import { WorkingDocumentDetailsDialogProgress } from "./WorkingDocumentDetailsDialogProgress";
import docDelete from "../../../../../assets/workingDocIcons/docDelete.svg";
import docChecked from "../../../../../assets/workingDocIcons/docChecked.svg";
import {
  RELEASE_DOCUMENT,
  RELEASE_DOCUMENT_TOOLTIP,
  DELETE_ONEDRIVE_DOC,
  DELETE_ONEDRIVE_DOC_TOOLTIP,
  ONEDRIVE_DOCUMENT_DELETED,
} from "../../../../../redux/constants/labelText";
import { DOC_CHECK_IN_TYPE } from "../../../../../redux/constants/metaData";
import { FILE_STATE } from "../../../../../redux/constants/metaData";
import { formatNumberAndRevision } from "../../../../../utils/formatNumberAndRevision";
import { getWorkingDocumentDisplayDocIdentifiers } from "../../../../../functions/helperMethod";
import { TruncatedTextWithTooltip } from "../../../../../components/common/TruncatedTextWithTooltip";

function isWordDocument(doc) {
  if (!doc) return false;
  const ext = (doc.FileExtension ?? doc.fileExtension ?? "").toUpperCase();
  return ext === "DOCX" || ext === "DOC" || ext === "DOCM" || ext === "DOTM";
}

function normalizeCheckStateText(text) {
  if (!text) return "-";
  return String(text)
    .replace(/\bChecked In\b/g, "Checked-in")
    .replace(/\bChecked Out\b/g, "Checked-out")
    .replace(/\bUndo Check Out\b/g, "Undo Check-out");
}

export function WorkingDocumentDetailsDialogContent({
  isDetailsComplete,
  isDetailsLoading,
  isDetailsError,
  isDetailsNoData,
  isCheckinMode = false,
  contentText,
  theme,
  workingIfsDocument = {},
  formatLastAction,
  selectedDocument,
  deleteChkBox,
  setDeleteChkBox,
  isOneDriveDocDeleteEnable,
  docReleaseStatus,
  onReleaseChange,
  viewCopyFiles,
  viewRequired,
  uploadViewCopyCount,
  isViewCopyExists = null,
  onCreateViewCopySelect,
  onViewCopyUploadChange,
  onSetDocumentToDeleteOneDrive,
}) {
  const isChecked = docReleaseStatus === "Released";
  const displayDocIds = getWorkingDocumentDisplayDocIdentifiers(selectedDocument);
  const displayDocNo = displayDocIds.docNo || workingIfsDocument.docNo;
  const displayDocRev = displayDocIds.docRev || workingIfsDocument.docRev;

  const labelCell = {
    width: "180px",
    maxWidth: "180px",
    fontWeight: "bold",
    paddingRight: "10px",
    verticalAlign: "top",
  };
  const valueCell = {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    verticalAlign: "top",
    minWidth: 0,
  };
  const titleCell = {
    verticalAlign: "top",
    minWidth: 0,
    maxWidth: "100%",
  };

  if (isDetailsComplete) {
    return (
      <DialogContent sx={{ overflowX: "hidden", maxWidth: "100%", boxSizing: "border-box", paddingTop: "5px" }}>
        <DialogContentText
          id="alert-dialog-description"
          component="div"
          sx={{ maxWidth: "100%", width: "100%", boxSizing: "border-box" }}
        >
        
          {!isCheckinMode && (
            <div style={{ minWidth: 0, maxWidth: "100%" }}>
              <table
                style={{
                  borderSpacing: "0 8px",
                  borderCollapse: "separate",
                  color: theme.colors.fontColor,
                  fontFamily: theme.fonts.regular,
                  fontSize: theme.fontSizes.normal,
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <tbody>
                  <tr>
                    <td valign="top" style={labelCell}>
                      Title:
                    </td>
                    <td valign="top" style={titleCell}>
                      <TruncatedTextWithTooltip text={workingIfsDocument.Title} />
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      Document No:
                    </td>
                    <td valign="top" style={valueCell}>
                      {formatNumberAndRevision(displayDocNo, displayDocRev)}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      Owner:
                    </td>
                    <td valign="top" style={valueCell}>
                      {workingIfsDocument.DocRespName}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      Class:
                    </td>
                    <td valign="top" style={valueCell}>
                      {`${workingIfsDocument.docClass} - ${workingIfsDocument.DocClassName}`}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      Format:
                    </td>
                    <td valign="top" style={valueCell}>
                      {workingIfsDocument.FormatSize != null &&
                      workingIfsDocument.FormatSize !== "" &&
                      workingIfsDocument.FormatSize !== "-"
                        ? `${workingIfsDocument.FormatSize} - ${workingIfsDocument.FormatSizeName}`
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      State:
                    </td>
                    <td valign="top" style={valueCell}>
                      {workingIfsDocument.State}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      File Status:
                    </td>
                    <td valign="top" style={valueCell}>
                      {normalizeCheckStateText(workingIfsDocument.FileState)}
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" style={labelCell}>
                      Modified:
                    </td>
                    <td valign="top" style={valueCell}>
                      {normalizeCheckStateText(formatLastAction(workingIfsDocument.LastAction))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {isCheckinMode && workingIfsDocument?.FileState === FILE_STATE.checkedOut && (
            <>
              <ViewCopyAccordion
                workingIfsDocument={workingIfsDocument}
                isViewCopyExists={isViewCopyExists}
                uploadFiles={workingIfsDocument}
                onCreateViewCopySelect={onCreateViewCopySelect}
                viewCopyFiles={viewCopyFiles}
                viewRequired={viewRequired}
                uploadViewCopyCount={uploadViewCopyCount}
                onViewCopyUploadChange={onViewCopyUploadChange}
                noTopMargin={isCheckinMode}
              />
              <Box
                sx={{
                  mt: "10px",
                  paddingLeft: "5px",
                  paddingRight: "16px",
                  height: "43px",
                  backgroundColor: "#E6F4EA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={isChecked}
                    onChange={onReleaseChange}
                    sx={{ color: "#4c8d6a", "&.Mui-checked": { color: "#4c8d6a" } }}
                  />
                  <Tooltip title={RELEASE_DOCUMENT_TOOLTIP} placement="top" arrow>
                    <Typography
                      sx={{
                        color: "#4c8d6a",
                        fontWeight: "bold",
                        fontFamily: theme.fonts?.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                        cursor: "default",
                      }}
                    >
                      {RELEASE_DOCUMENT}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box component="img" src={docChecked} alt="icon" sx={{ width: 25, height: 25 }} />
              </Box>
              <Box
                sx={{
                  mt: "10px",
                  paddingLeft: "5px",
                  paddingRight: "16px",
                  height: "43px",
                  backgroundColor: "#F0EEE2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {!isOneDriveDocDeleteEnable && (
                    <Checkbox
                      onChange={(ev) => {
                        setDeleteChkBox(ev.target.checked);
                        onSetDocumentToDeleteOneDrive({
                          checkinSuccess: false,
                          id: selectedDocument?.id,
                          deleteRequired: isOneDriveDocDeleteEnable ? true : ev.target.checked,
                          checkinType: DOC_CHECK_IN_TYPE.checkinExisting,
                        });
                      }}
                      sx={{ color: "#AE9B2E", "&.Mui-checked": { color: "#AE9B2E" } }}
                      checked={isOneDriveDocDeleteEnable ? true : deleteChkBox}
                      disabled={isOneDriveDocDeleteEnable}
                    />
                  )}
                  <Tooltip title={DELETE_ONEDRIVE_DOC_TOOLTIP} placement="top" arrow>
                    <Typography
                      sx={{
                        color: "#AE9B2E",
                        fontWeight: "bold",
                        fontFamily: theme.fonts?.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                        cursor: "default",
                      }}
                    >
                      {isOneDriveDocDeleteEnable ? (
                        <span style={{ marginLeft: 10 }}>{ONEDRIVE_DOCUMENT_DELETED}</span>
                      ) : (
                        DELETE_ONEDRIVE_DOC
                      )}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box
                  component="img"
                  src={docDelete}
                  alt="icon"
                  sx={{ width: 30, height: 30, opacity: isOneDriveDocDeleteEnable ? "0.5" : "initial" }}
                />
              </Box>
            </>
          )}
        </DialogContentText>
      </DialogContent>
    );
  }

  return (
    <DialogContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "56px",
        textAlign: "center",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 400,
          fontFamily: "Poppins, sans-serif",
          marginBottom: "4px",
          color: theme.colors.fontColor,
        }}
      >
        {contentText}
      </Typography>
      <WorkingDocumentDetailsDialogProgress
        isDetailsNoData={isDetailsNoData}
        isDetailsError={isDetailsError}
        isDetailsLoading={isDetailsLoading}
        theme={theme}
      />
    </DialogContent>
  );
}
